import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './Auth.css';

function ResendButton() {
  const [status, setStatus] = useState('idle');
  const handleResend = async () => {
    setStatus('sending');
    try {
      await axios.post('/api/auth/resend-verification');
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };
  if (status === 'sent') return <p style={{ color:'#4ade80', fontSize:'0.875rem', marginTop:'16px' }}>✓ Verification email sent — please check your inbox.</p>;
  if (status === 'error') return <p style={{ color:'var(--error)', fontSize:'0.875rem', marginTop:'16px' }}>Could not resend. Please log in and try from your account page.</p>;
  return (
    <button className="btn btn-outline" style={{ marginTop:'16px' }} onClick={handleResend} disabled={status === 'sending'}>
      {status === 'sending' ? 'Sending...' : 'Resend Verification Email'}
    </button>
  );
}

const ROLE_HOME = { admin: '/admin', instructor: '/instructor', parent: '/dashboard' };

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const token = searchParams.get('token');
  const [status, setStatus] = useState(token ? 'ready' : 'error');
  const [message, setMessage] = useState(token ? '' : 'Invalid verification link.');
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const res = await axios.get('/api/auth/verify-email', { params: { token } });
      const verifiedUser = res.data.user;
      setStatus('success');
      if (user) {
        updateUser({ email_verified: true });
      }
      const role = verifiedUser?.role || user?.role;
      setTimeout(() => navigate(ROLE_HOME[role] || '/dashboard'), 2000);
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.error || 'Verification failed. Please request a new link.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-branding">
        <div className="auth-branding-inner">
          <div className="auth-logo"><img src="/wf-logo.png" alt="Wow Factor Baseball" style={{width:'192px',height:'192px',objectFit:'contain'}} /></div>
          <h1 className="auth-brand-name">WOW FACTOR</h1>
          <p className="auth-brand-sub">Baseball Training & Development</p>
          <div className="auth-brand-divider" />
          <p className="auth-brand-quote">"Elevating the game,<br />one session at a time."</p>
        </div>
      </div>
      <div className="auth-form-panel">
        <div className="auth-form-container">
          {status === 'ready' && (
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'3rem', marginBottom:'16px' }}>✉</div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:'2rem', letterSpacing:'0.05em', marginBottom:'12px' }}>VERIFY YOUR EMAIL</h2>
              <p style={{ color:'var(--text-secondary)', marginBottom:'28px' }}>Click the button below to confirm your email address and activate your account.</p>
              <button className="btn btn-primary" style={{ display:'inline-flex', alignItems:'center', gap:'8px' }} onClick={handleVerify} disabled={verifying}>
                {verifying && <span className="auth-spinner" style={{ width:'16px', height:'16px', borderWidth:'2px', display:'inline-block' }} />}
                {verifying ? 'Verifying...' : 'Verify My Email'}
              </button>
            </div>
          )}
          {status === 'success' && (
            <div style={{ textAlign:'center' }}>
              <div style={{ width:'72px', height:'72px', borderRadius:'50%', background:'rgba(74,222,128,0.15)', border:'2px solid #4ade80', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2rem', margin:'0 auto 20px' }}>✓</div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:'2rem', letterSpacing:'0.05em', marginBottom:'12px' }}>EMAIL VERIFIED!</h2>
              <p style={{ color:'var(--text-secondary)', marginBottom:'8px' }}>Your account is now active.</p>
              <p style={{ color:'var(--text-muted)', fontSize:'0.85rem', marginBottom:'24px' }}>
                Taking you to your dashboard...
              </p>
              <Link to="/dashboard" className="btn btn-primary" style={{ display:'inline-flex' }}>
                Go to Dashboard
              </Link>
            </div>
          )}
          {status === 'error' && (
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'3rem', marginBottom:'16px' }}>✉</div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.8rem', letterSpacing:'0.05em', marginBottom:'12px', color:'var(--error)' }}>VERIFICATION FAILED</h2>
              <p style={{ color:'var(--text-secondary)', lineHeight:'1.6', marginBottom:'16px' }}>{message}</p>
              <p style={{ color:'var(--text-muted)', fontSize:'0.85rem', marginBottom:'20px' }}>
                If your link has expired, you can request a new one below.
              </p>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'10px' }}>
                <ResendButton />
                <Link to="/login" className="btn btn-outline" style={{ display:'inline-flex' }}>Go to Login</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
